import { EditSectionTemplate } from "components/MainSectionTemplate/EditSectionTemplate";
import { SomethingUnexpectedHappenedMessageBox } from "components/MessageBox/SomethingUnexpectedHappenedMessageBox";
import { Loading } from "components/panels";
import { useMutation } from "@tanstack/react-query";
import { InvoiceV2Api } from "queries/InvoiceV2";
import React from "react";
import moment from "moment"
import { Checkbox } from "components/Atoms/inputs/Checkbox";
import SubLabel from "components/Atoms/Sublabel";
import { FormDatePicker } from "components/DatePicker/SingleDatePicker";
import { getInvoiceDueDate } from "utilities/invoiceUtils";
import DocumentDueDatePicker from "components/Document/DocumentDueDatePicker";
interface Props {
  documentId: number;
  dueInDays: number;
}

const SendOutDocument = (props: Props) => {
  const preChangeToOutstandingInfoApi = InvoiceV2Api.GetPreChangeToOutstandingInfo.useQuery(props.documentId, {});
  const changeToOutstandingMutation = useMutation({
    mutationFn: window.CoreApi.InvoiceV2.ChangeToOutstanding,
    onSuccess: res => {
      if (res.data.hasErrors) {
        window.displayGenericModal(RESX.GeneralWarnings.Warning, res.data.errors[0].message, "tiny", "red");
      } else {
        window.location.reload();
      }
    }
  });
  const [useToday, setUseToday] = React.useState(true);
  const [invoiceDate, setInvoiceDate] = React.useState<moment.Moment>(moment.utc().startOf("day"));
  const [dueInDays, setDueInDays] = React.useState<number>(props.dueInDays);


  if (preChangeToOutstandingInfoApi.isLoading) {
    return <Loading />;
  }

  if (preChangeToOutstandingInfoApi.isError) {
    return <SomethingUnexpectedHappenedMessageBox />
  }

  const submit = async () => {
    return changeToOutstandingMutation.mutateAsync({ invoiceId: props.documentId, dueInDays, invoiceDate: invoiceDate.toDate() });
  }

  const minDate = moment.utc(preChangeToOutstandingInfoApi.data.data.minInvoiceDate);
  const daysBetween = moment.utc().startOf("day").diff(minDate, "days");

  const daysLabel = daysBetween === 1 ? RESX.DateTime.YDay : `${daysBetween} ${RESX.DateTime.days}`;
  const message = preChangeToOutstandingInfoApi.data.data.invoiceDateMessage.replace("{0}", daysLabel);

  const dueDate = getInvoiceDueDate(invoiceDate, dueInDays);

  const onInvoiceDateChange = (newInvoiceDate: moment.Moment) => {
    setInvoiceDate(newInvoiceDate);

    // The due date stays fixed. So we need to adjust dueInDays accordingly.
    const newDueInDays = dueDate.diff(newInvoiceDate, "days");
    setDueInDays(newDueInDays);
  }

  return <EditSectionTemplate
    submit={submit}
    noPanels
    onSuccess={() => { }}
    sections={[
      {
        key: "invoiceDateSection",
        type: "regular",
        title: "",
        fields: [{
          key: "invoiceDateCheckbox",
          label: RESX.Invoice.InvoiceDate,
          hidden: !preChangeToOutstandingInfoApi.data.data.canChangeInvoiceDate || !useToday,
          renderEditNew: () => (
            <Checkbox
              checked={useToday}
              label={<div>
                <div>{RESX.DateTime.Today}</div>
                <SubLabel small>{message}</SubLabel>
              </div>}
              onChange={(_, checked) => setUseToday(checked)}
            />
          )
        }, {
          key: "invoiceCantChangeDate",
          label: RESX.Invoice.InvoiceDate,
          hidden: preChangeToOutstandingInfoApi.data.data.canChangeInvoiceDate,
          renderEditNew: () => (
            <div className="main-section-template-fields-padding">
              <div>{RESX.DateTime.Today}</div>
              <SubLabel small>{message}</SubLabel>
            </div>
          )
        }, {
          key: "invoiceDatePicker",
          label: RESX.Invoice.InvoiceDate,
          hidden: useToday || !preChangeToOutstandingInfoApi.data.data.canChangeInvoiceDate,
          renderEditNew: () => (
            <FormDatePicker
              value={invoiceDate}
              minDate={minDate}
              onChange={onInvoiceDateChange}
              validatorKey="invoiceDatePicker"
              maxDate={moment.utc()}
            />
          )
        }, {
          key: "dueDate",
          label: RESX.DateTime.DueDate,
          renderEditNew: () => (
            <div className="flex">
              <DocumentDueDatePicker
                onChange={(_, dueInDays) => setDueInDays(dueInDays)}
                minDate={moment.utc().add(1, "day").startOf("day")}
                sendDate={invoiceDate}
                dueInDays={dueInDays}
              />
              <div className="main-section-template-fields-padding">
                <SubLabel>{dueDate.fromNow()}</SubLabel>
              </div>
            </div>
          )
        }, {
          key: "warningMessage",
          label: "",
          hideLabel: true,
          renderEditNew: () => (
            <div className="main-section-template-fields-padding">
              <div className="ui message purple">
                {RESX.Invoice.YouWontBeAbleToMakeChanges}
              </div>
            </div>
          )
        }]
      }
    ]}
  />
};

export default SendOutDocument;